import type { BlockCoverage, CommitResult, PanelProgress, World } from '../shared/types';
import {mergePlacementRows,type LayerActionResult} from '../shared/moderation';

export function uniqueById<T extends {id:string|number}>(items:T[]):T[]{return [...new Map(items.map(item=>[item.id,item])).values()];}
export function mergeCoverage(previous:BlockCoverage[],incoming:BlockCoverage[]):BlockCoverage[]{
  const coverage=new Map(previous.map(item=>[item.blockId,item]));
  for(const item of incoming){const old=coverage.get(item.blockId);if(!old||item.coveredCells>=old.coveredCells)coverage.set(item.blockId,item);}
  return [...coverage.values()];
}
export function mergePanelProgress(previous:PanelProgress[],incoming:PanelProgress[]):PanelProgress[]{
  const progress=new Map(previous.map(item=>[item.blockId,item]));
  for(const item of incoming){const old=progress.get(item.blockId);if(!old||item.qualifiedPanels>=old.qualifiedPanels)progress.set(item.blockId,item);}
  return [...progress.values()];
}
// Moderation tokens supersede artwork by revision; old receipts cannot revive it.
export function mergeWorld(previous:World,incoming:World):World{
  if((incoming.worldEpoch??0)<(previous.worldEpoch??0))return previous;
  if((incoming.worldEpoch??0)>(previous.worldEpoch??0))return incoming;
  return {...incoming,frontier:Math.max(previous.frontier,incoming.frontier),blocks:uniqueById([...previous.blocks,...incoming.blocks]),assets:uniqueById([...previous.assets,...incoming.assets]),surfaces:uniqueById([...previous.surfaces,...incoming.surfaces]),...(previous.moderationRevision!==undefined||incoming.moderationRevision!==undefined?{moderationRevision:Math.max(previous.moderationRevision??0,incoming.moderationRevision??0)}:{}),placements:mergePlacementRows(previous.placements,incoming.placements),coverage:mergeCoverage(previous.coverage,incoming.coverage),panelProgress:mergePanelProgress(previous.panelProgress,incoming.panelProgress)};
}

/** A live commit carries only its newly opened descriptor. After a reconnect it
 * may jump past an unseen predecessor. Keep a coherent geometry prefix until
 * the already-scheduled complete snapshot arrives; never guess missing widths.
 * Artwork/history evidence still accumulates while that metadata is fetched. */
export function mergeWorldCommit(previous:World,incoming:Pick<CommitResult,'placement'|'coverage'|'panelProgress'|'unlockedBlock'>):World {
  const next=incoming.unlockedBlock;
  const append=!!next&&next.id===previous.frontier+1&&previous.blocks.length===next.id;
  return {...previous,
    placements:mergePlacementRows(previous.placements,[incoming.placement]),
    coverage:mergeCoverage(previous.coverage,[incoming.coverage]),
    panelProgress:mergePanelProgress(previous.panelProgress,[incoming.panelProgress]),
    frontier:append?next.id:previous.frontier,
    blocks:append?[...previous.blocks,next]:previous.blocks,
  };
}

export function mergeWorldModeration(previous:World,result:LayerActionResult):World {
  if(result.worldEpoch!==(previous.worldEpoch??0))return previous;
  return {...previous,moderationRevision:Math.max(previous.moderationRevision??0,result.moderationRevision),placements:mergePlacementRows(previous.placements,[result.placement])};
}
